import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IconButton,
  TextInput,
  type TextInputProps,
} from "@prophero-ds/cx-react";

import styles from "./PasswordInput.module.css";

type PasswordInputProps = Omit<TextInputProps, "type"> & {
  /** Accessible name of the toggle while the password is hidden. */
  showLabel?: string;
  /** Accessible name of the toggle while the password is visible. */
  hideLabel?: string;
};

/**
 * CX `TextInput` with a show/hide password toggle. Local until CX ships a
 * trailing slot on `TextInput`.
 */
const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ showLabel, hideLabel, disabled, className, ...props }, ref) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(false);

    return (
      <div className={styles.root}>
        <TextInput
          ref={ref}
          type={isVisible ? "text" : "password"}
          disabled={disabled}
          className={className}
          {...props}
        />
        <IconButton
          type="button"
          className={styles.toggle}
          variant="ghost"
          tone="neutral"
          size="small"
          icon={{ name: isVisible ? "no-eye" : "eye" }}
          aria-label={
            isVisible
              ? (hideLabel ?? t("common.hidePassword"))
              : (showLabel ?? t("common.showPassword"))
          }
          aria-pressed={isVisible}
          disabled={disabled}
          onClick={() => setIsVisible((prev) => !prev)}
          data-testid="password-input-toggle"
        />
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
